import { ICreateEventForm, IUpdateEvent, IFile } from '@sdk/contracts';
import { Carousel, Form, Input } from 'antd';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { TagsWithCourses } from '../../../TagsWithCourses';

import {
  TabContent,
  EventSection,
  SecondaryTitle,
  SecondaryTitleWrapper,
  EmojiWrapper,
  DocumentDisplayWrapper,
  VideoPreviewContainer
} from './DetailTab.styles';

import { DocumentDisplay } from '@/components/DocumentDisplay';
import { HtmlEditor } from '@/components/Form';
import { Upload } from '@/components/Upload';
import { VideoPlayer } from '@/components/VideoPlayer';
import { RULES } from '@/constants';
import { useLiveClassDrawer } from '@/pages/Common/Events/store/useLiveClassDrawer';
import { useEventDetail, useMutateEvents, useProfile } from '@/store';

export type DetailTabHandle = {
  submit: () => void;
  deleteEvent: () => void;
};

export const DetailTab = forwardRef<DetailTabHandle>(function DetailTabImpl(_props, ref) {
  const [form] = Form.useForm<ICreateEventForm>();
  const { t } = useTranslation('CommonEventsTranslations');
  const { event, close } = useLiveClassDrawer();
  const {
    roleCheck: { isManager }
  } = useProfile();
  const eventId = event?._id;
  const { event: fullEvent } = useEventDetail(eventId);
  const { updateEvent, removeEvent, addAttachment, removeAttachment, removeLiveRecord } =
    useMutateEvents();

  const handleFinish = async (values: ICreateEventForm) => {
    if (!fullEvent) return;

    const { title, description, tagsWithCourses } = values;

    const updatePayload: IUpdateEvent = {
      _id: fullEvent._id,
      title,
      description,
      type: fullEvent.type,
      recurringRules: fullEvent.recurringRules,
      startDate: fullEvent.startDate,
      endDate: fullEvent.endDate,
      tagsWithCourses:
        tagsWithCourses && tagsWithCourses.filter(tagWithCourses => !!tagWithCourses.tag).length > 0
          ? tagsWithCourses.map(tagWithCourses => ({
              tag: tagWithCourses.tag,
              coursesWithSections:
                tagWithCourses.coursesWithSections?.map(courseWithSections => ({
                  course: courseWithSections.course._id,
                  sections: courseWithSections.sections.map(section => section._id)
                })) ?? []
            }))
          : [],
      updateAllRecurring: false
    };

    updateEvent(updatePayload, { onSuccess: close });
  };

  const handleUpload = (f: IFile | IFile[] | null) => {
    if (!fullEvent?._id || !f) return;
    const file = Array.isArray(f) ? f[0] : f;
    addAttachment({ eventId: fullEvent._id, file });
  };

  const handleDeleteAttachment = (id: string) => {
    if (!fullEvent?._id) return;
    removeAttachment({ eventId: fullEvent._id, attachmentId: id });
  };

  const handleDeleteRecord = (id: string) => {
    if (!fullEvent?._id) return;
    removeLiveRecord({ eventId: fullEvent._id, liveRecordId: id });
  };

  useEffect(() => {
    if (!fullEvent) return;

    form.setFieldsValue({
      title: fullEvent.title,
      description: fullEvent.description,
      tagsWithCourses: fullEvent.tagsWithCourses.length
        ? fullEvent.tagsWithCourses.map(tagWithCourses => ({
            tag: tagWithCourses.tag._id,
            coursesWithSections: tagWithCourses.coursesWithSections
          }))
        : [{}]
    });
  }, [fullEvent, form]);

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    deleteEvent: () => {
      if (!fullEvent) return;
      removeEvent({ eventId: fullEvent._id, removeRecurring: false });
    }
  }));

  const attachments = fullEvent?.attachments || [];
  const liveRecords = (fullEvent?.liveRecords || []).filter(r => r.status === 'READY');

  return (
    <TabContent>
      <Form<ICreateEventForm> layout="vertical" onFinish={handleFinish} form={form}>
        {liveRecords.length > 0 && (
          <EventSection>
            <Carousel dots={liveRecords.length > 1} arrows infinite={false}>
              {liveRecords.map(file => (
                <VideoPreviewContainer key={file._id}>
                  <VideoPlayer file={file} />
                </VideoPreviewContainer>
              ))}
            </Carousel>
          </EventSection>
        )}

        <Form.Item name="_id" hidden>
          <Input />
        </Form.Item>

        <Form.Item name="title" label={t('TITLE')} rules={[RULES.REQUIRED]}>
          <Input />
        </Form.Item>

        <Form.Item name="description" label={t('DESCRIPTION')} rules={[RULES.REQUIRED]}>
          <HtmlEditor />
        </Form.Item>

        <TagsWithCourses form={form} />

        <EventSection>
          <SecondaryTitleWrapper>
            <SecondaryTitle>
              <EmojiWrapper>
                <span role="img" aria-label={t('DOCUMENTS')}>
                  📄
                </span>
              </EmojiWrapper>
              {t('DOCUMENTS')}
            </SecondaryTitle>
            <Upload
              mode="upload"
              showUploadList={false}
              icon="Upload"
              label={t('UPLOAD_ATTACHMENT')}
              onChange={handleUpload}
            />
          </SecondaryTitleWrapper>
          <DocumentDisplayWrapper>
            {attachments.length > 0 &&
              attachments.map(file => (
                <DocumentDisplay
                  key={file._id}
                  file={file}
                  isEditable={true}
                  handleDelete={handleDeleteAttachment}
                />
              ))}
          </DocumentDisplayWrapper>
        </EventSection>

        <EventSection>
          <SecondaryTitleWrapper>
            <SecondaryTitle>
              <EmojiWrapper>
                <span role="img" aria-label={t('RECORDS')}>
                  📹
                </span>
              </EmojiWrapper>
              {t('RECORDS')}
            </SecondaryTitle>
            {/* TODO: Add and supportupload record button */}
          </SecondaryTitleWrapper>
          {isManager && (
            <DocumentDisplayWrapper>
              {liveRecords.length > 0 &&
                liveRecords.map(file => (
                  <DocumentDisplay
                    key={file._id}
                    type={'.mp4'}
                    file={file}
                    isEditable={true}
                    handleDelete={handleDeleteRecord}
                  />
                ))}
            </DocumentDisplayWrapper>
          )}
        </EventSection>
      </Form>
    </TabContent>
  );
});
